import React, { useState } from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { useSandboxSession } from '../hooks/useSandboxSession';
import { useDemoLeadGate } from '../hooks/useDemoLeadGate';

/**
 * SandboxExpiryBanner — sticky banner shown at the top of the dashboard
 * for sandbox users when they have less than 2 hours left before the
 * 24h purge. Clicking "Save my work" opens the DemoLeadCaptureModal so
 * the user can convert their sandbox into a tracked lead.
 *
 * The banner is dismissible per-session (sessionStorage) but reappears
 * every fresh tab open while time remains.
 */
export function SandboxExpiryBanner() {
  const { showExpiryBanner, msRemaining, leadCaptured, isSandbox } = useSandboxSession();
  const { guard, modal } = useDemoLeadGate();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('sandbox-banner-dismissed') === '1',
  );

  // Always-visible banner (less intrusive) while in sandbox + time > 2h
  const showInfoBanner = isSandbox && !showExpiryBanner && !dismissed;

  if (!isSandbox) return null;

  const dismiss = () => {
    sessionStorage.setItem('sandbox-banner-dismissed', '1');
    setDismissed(true);
  };

  const mins = Math.floor(msRemaining / 60_000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const timeStr = h > 0 ? `${h}h ${m}m` : `${m}m`;

  // High-urgency banner (< 2h)
  if (showExpiryBanner) {
    return (
      <>
        <div
          data-testid="sandbox-expiry-banner"
          className="sticky top-14 z-30 md:ml-64 px-4 py-2.5 flex items-center justify-between gap-3 text-sm font-medium shadow-md"
          style={{ background: 'linear-gradient(90deg,#b45309 0%,#f59e0b 100%)', color: '#fff' }}
        >
          <div className="flex items-center gap-2.5">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>
              <strong>{timeStr}</strong> left before this sandbox is wiped. Save your testing profile to keep your work.
            </span>
          </div>
          <button
            type="button"
            onClick={() =>
              guard(() => {}, { reason: 'convert your sandbox into a permanent trial' })
            }
            disabled={leadCaptured}
            data-testid="sandbox-expiry-cta"
            className="bg-white text-amber-700 font-semibold text-xs px-3 py-1.5 rounded-md hover:bg-slate-100 transition disabled:opacity-60"
          >
            {leadCaptured ? 'Saved ✓' : 'Save my work'}
          </button>
        </div>
        {modal}
      </>
    );
  }

  // Soft info banner (> 2h remaining)
  if (showInfoBanner) {
    return (
      <div
        data-testid="sandbox-info-banner"
        className="sticky top-14 z-30 md:ml-64 px-4 py-2 flex items-center justify-between gap-3 text-xs"
        style={{ background: '#0f172a', color: '#f1f5f9' }}
      >
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5" style={{ color: '#f59e0b' }} />
          <span>
            You're exploring a 24-hour demo sandbox. <span className="opacity-70">~{timeStr} remaining.</span>
          </span>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-slate-400 hover:text-white text-xs"
          data-testid="sandbox-info-banner-dismiss"
        >
          Got it ✕
        </button>
      </div>
    );
  }

  return null;
}

export default SandboxExpiryBanner;
